<script setup lang="ts">
import type { CourseRoleId } from '~/data/courseRoles'

// get params
const route = useRoute()
const params = route.params

// get job
const jobId = (params.id as string).split('---')[0]
const { getJobReactive, listJobOnce, saveJob, unSaveJob } = useJob()

const { data: jobData, refresh: refreshJob } = await getJobReactive(jobId, { lazy: false })
const job = ref(jobData)

if (!job.value) {
  throw createError({
    statusCode: 404,
    message: 'Job not found',
    fatal: true
  })
}

// config
const runtimeConfig = useRuntimeConfig()

const siteUrl = runtimeConfig.public.siteUrl
const pageUrl = `${siteUrl}${getJobPageUrl(job.value.id_slug, job.value.title)}`

const ogTitle = `${job.value.title} | ${runtimeConfig.public.siteName}`
const ogDescription = 'Explore other jobs at TalentLabs!'

useSeoMeta({
  title: () => ogTitle,
  description: () => ogDescription,
  ogSiteName: () => ogTitle,
  ogTitle: () => ogTitle,
  ogDescription: () => ogDescription,
  ogUrl: () => pageUrl,
  // ogImageUrl: () => ogImageUrl,
  ogImageHeight: 630,
  ogImageWidth: 1200,
  ogImageAlt: () => ogTitle,
  ogImageType: 'image/png',
  // ogImageSecureUrl: () => ogImageUrl,
  twitterCard: 'summary_large_image',
  twitterTitle: ogTitle,
  twitterDescription: () => ogDescription
  // twitterImage: () => ogImageUrl
})
useHead({
  link: [
    {
      rel: 'canonical',
      href: pageUrl
    }
  ]
})

definePageMeta({
  layout: 'student'
})

// use popup
const show = ref(false)
const { closePopup, showPopup } = usePopup()

const handleShowPopup = () => showPopup(show)

const handleClosePopup = async () => {
  await refreshJob()
  closePopup(show)
}

// user
// only authenticated users can see the job application screen
const { status, data: user } = useAuth()

// get jobs with similar role
const similarJobsQuery: Parameters<typeof listJobOnce>[0] = {
  page_size: 4
}
if (job.value.role) {
  similarJobsQuery.role = [job.value.role]
}

const similarJobs = (await listJobOnce(similarJobsQuery)).data

// get jobs with similar employer
const otherJobsQuery: Parameters<typeof listJobOnce>[0] = {
  page_size: 4
}
if (job.value.company_part) {
  otherJobsQuery.company__id_slug = [job.value.company_part.id_slug]
}
const otherJobs = (await listJobOnce(otherJobsQuery)).data

const clickApply = () => {
  if (status.value === 'authenticated') {
    handleShowPopup()
  } else {
    navigateTo({
      path: '/login',
      query: {
        next: 'apply_job',
        jobId: params.id
      }
    })
  }
}

// BACKEND - applyJob
async function clickBookmark() {
  if (status.value !== 'authenticated') {
    return navigateTo({
      path: '/login',
      query: {
        next: 'save_job',
        jobId: params.id
      }
    })
  }

  if (!job.value) return

  if (hasSaved.value) {
    if (!job.value.job_activity_part?.saved_record_id_slug) return
    await unSaveJob(job.value.job_activity_part?.saved_record_id_slug)
  } else {
    await saveJob(job.value.id_slug)
  }
  // refetch job
  await refreshJob()
}

const hasSaved = computed(() => Boolean(job.value?.job_activity_part?.saved_record_id_slug))
const hasApplied = computed(() => Boolean(job.value?.job_activity_part?.applied_record_id_slug))
const hasClosed = computed(() => job.value?.status !== 'PUBLISHED')

const exclusivityText = computed(() => {
  if (job.value?.exclusive_to_user_tags) {
    if (job.value?.exclusive_to_user_tags.includes('KYDP 2024 (A)')) return 'Exclusive to KYDP 2024'
    if (job.value?.exclusive_to_user_tags.includes('KYDP 05-2024 (A)'))
      return 'Exclusive to KYDP 2024 (A) Cohort 1'
    if (job.value?.exclusive_to_user_tags.includes('KYDP 06-2024 (A)'))
      return 'Exclusive to KYDP 2024 (A) Cohort 2'
  }
  return ''
})

const hideApply = computed(() => {
  // Check if job is exclusive to some users
  if (job.value?.exclusive_to_user_tags && job.value.exclusive_to_user_tags.length > 0) {
    if (status.value === 'unauthenticated') return false
    if (
      (user.value?.secret_part?.tags || []).some((tag) =>
        (job.value?.exclusive_to_user_tags || []).includes(tag)
      )
    )
      return false
    return true
  }
  return false
})
</script>

<template>
  <div id="job" class="container">
    <div v-if="job" id="job-header">
      <div class="breadcrumbs">
        <NuxtLink to="/">Home</NuxtLink>
        <b>></b>
        <NuxtLink to="/jobs?page=1">Jobs</NuxtLink>
        <b>></b>
        <a>{{ job.title }} </a>
      </div>

      <div id="job-overview">
        <h1>
          {{ job.title }}
        </h1>
        <div>
          <h6 v-if="exclusivityText">{{ exclusivityText }}</h6>
        </div>
        <NuxtLink
          v-if="job.company_part"
          id="job-overview-employer"
          class="clickable"
          :to="`/employers/${job.company_part?.id_slug}`"
          :alt="job.company_part?.name || 'Company'"
        >
          <img
            :src="toCompanyIconUrl(job.company_part?.logo_part?.object_key)"
            :alt="job.company_part?.name || 'Company Logo'"
          >

          <span> {{ job.company_part?.name }}</span>
        </NuxtLink>

        <div id="job-overview-summary">
          <div v-if="job.location" id="job-overview-summary-location" class="job-summary-item">
            <CommonIconJobsIconLocationOutline />
            <span>{{ getReadableJobLocation(job.location) }}</span>
          </div>

          <div v-if="job.mode" class="job-summary-item">
            <CommonIconJobsIconBriefcase />
            <span>{{ getReadableJobWorkMode(job.mode) }}</span>
          </div>

          <div v-if="job.work_mode" class="job-summary-item">
            <CommonIconJobsIconBuilding />
            <span>{{ getReadableJobWorkMode(job.work_mode) }} </span>
          </div>

          <div v-if="job.role" class="job-summary-item">
            <CommonIconJobsIconSpanner />
            <span>{{ getReadableJobRole(job.role as CourseRoleId) }}</span>
          </div>

          <div v-if="job.posting_date" class="job-summary-item">
            <CommonIconJobsIconTime />
            <span>{{ getReadableDayDifferenceFromToday(job.posting_date) }}</span>
          </div>
        </div>
      </div>

      <div v-if="!hideApply" id="job-header-btns">
        <CommonButton :black="!hasSaved" :outline="!hasSaved" @click="clickBookmark">
          <CommonIconJobsIconBookmark />
          {{ hasSaved ? 'Saved' : 'Save' }}
        </CommonButton>

        <CommonButton :disabled="hasClosed" black @click="clickApply">
          {{ hasApplied ? 'Applied' : 'Apply' }}
        </CommonButton>
      </div>
    </div>

    <div v-if="job" id="job-content">
      <div id="job-content-description">
        <div v-html="job.description" />

        <div v-if="!hideApply" id="job-content-description-btn">
          <CommonButton :disabled="hasClosed" large @handle-click="clickApply">
            {{ hasApplied ? 'Applied' : 'Apply now' }}
          </CommonButton>
        </div>
      </div>

      <div v-if="similarJobs.length !== 0" id="job-content-similar">
        <h2>Similar jobs</h2>
        <template v-for="similarJob in similarJobs" :key="similarJob.id_slug">
          <PageJobsSimilarJobCard v-if="similarJob.id_slug !== job.id_slug" :job="similarJob" />
        </template>
      </div>
    </div>

    <div v-if="job?.company_part" id="job-company">
      <h2>More about {{ job.company_part.name }}</h2>

      <NuxtLink
        id="job-company-card"
        :to="`/employers/${job.company_part?.id_slug}`"
        :alt="job.company_part.name"
      >
        <div id="job-company-card-title">
          <img
            :src="toCompanyIconUrl(job.company_part?.logo_part?.object_key)"
            :alt="job.company_part.name"
          >
          <h1>{{ job.company_part.name }}</h1>
        </div>

        <div v-if="job.company_part.location" class="job-company-detail one-line">
          <CommonIconJobsIconLocationOutline />
          {{ getReadableJobLocation(job.company_part.location) }}
        </div>

        <div v-if="job.company_part.industry" class="job-company-detail">
          <CommonIconJobsIconBuilding />
          {{ getReadableJobIndustry(job.company_part.industry) }}
        </div>

        <div v-if="job.company_part.scale" class="job-company-detail">
          <CommonIconJobsIconPeople />
          {{ job.company_part.scale }}
        </div>

        <div id="job-company-card-btn">
          More
          <CommonIconArrow />
        </div>
      </NuxtLink>

      <div v-if="otherJobs.length !== 0">
        <h2>Other jobs from {{ job.company_part.name }}</h2>

        <template v-for="otherJob in otherJobs" :key="otherJob.id_slug">
          <PageJobsVerticalJobCard
            v-if="otherJob.id_slug !== job.id_slug"
            :job="otherJob"
            :has-applied="false"
            :has-saved="false"
            :auth="status === 'authenticated'"
          />
        </template>
      </div>
    </div>

    <LayoutNavbarMobileBackButton />

    <PageJobsApplyJobScreen
      v-if="job && !hideApply && status === 'authenticated'"
      :show="show"
      :job="job"
      @show-popup="handleShowPopup"
      @close-popup="handleClosePopup"
    />
  </div>
</template>

<style scoped lang="scss">
#job {
  overflow-x: hidden;
  margin: auto;
  width: 1000px;

  h2 {
    font-size: 0.95rem;
    color: var(--textMedium);
    margin-bottom: 20px;
  }

  h6 {
    $size: 0.8rem;
    font-size: $size;
    line-height: $size;
    font-weight: 500;
    background-color: rgb(214, 255, 230);
    color: rgb(0, 152, 58);
    padding: 4px 8px;
    border-radius: 5px;
    display: inline-flex;
    margin-bottom: 10px;
  }

  &-header {
    display: flex;
    padding-bottom: 50px;
    border-bottom: 1px solid var(--borderMedium);
    flex-wrap: wrap;

    .breadcrumbs {
      margin: 10px 0 45px;
    }

    &-btns {
      display: flex;
      gap: 10px;
      padding-top: 5px;

      .btn-m {
        height: 50px;
        display: flex;
        align-items: center;
        justify-content: center;
      }

      svg {
        $size: 15px;
        height: $size;
        width: $size;
        margin: auto;
        margin-right: 10px;
      }
    }
  }

  &-overview {
    flex: 1;

    &-employer {
      display: inline-flex;
      margin-top: 15px;
      align-items: center;

      img {
        $size: 45px;
        height: $size;
        width: $size;
        border-radius: 20%;
        margin-right: 10px;
        border: 1px solid gainsboro;
        object-fit: contain;
      }

      span {
        font-size: 1.1rem;
        transition: color 0.3s ease-out;
      }
    }

    &-summary {
      margin-top: 25px;
      display: flex;
      flex-wrap: wrap;
      row-gap: 15px;
      column-gap: 35px;

      .job-summary-item {
        display: flex;
        align-items: center;
        font-size: 0.95rem;

        svg {
          $size: 14px;
          height: $size;
          width: $size;
          margin-right: 12px;
          fill: var(--textMedium);
        }
      }

      &-location {
        width: 100%;
      }
    }
  }

  &-content {
    display: grid;
    grid-template-columns: 1fr 310px;
    gap: 100px;
    padding: 40px 0 50px;

    &-description:deep(h1, h2, h3, h4, h5, h6) {
      font-size: 1.2rem;
      margin: 0 0 15px;
    }

    &-description:deep(li) {
      margin-left: 14px;
      padding-left: 10px;
      margin-bottom: 10px;
    }

    &-description {
      font-size: 1rem;
      line-height: 1.5rem;

      &-btn {
        margin: 50px auto 0;
        text-align: center;
      }
    }
  }

  &-company {
    border-top: 1px solid var(--borderMedium);
    padding: 50px 0;
    width: 70%;

    &-card {
      $padding: 30px;

      border: 1px solid var(--borderMedium);
      border-radius: 20px;
      margin-bottom: 50px;
      padding: $padding;
      display: flex;
      flex-wrap: wrap;
      column-gap: 50px;
      transition: border 0.3s ease-out;
      position: relative;

      &-title {
        display: flex;
        align-items: center;
        width: 100%;
        margin-bottom: 25px;

        img {
          $size: 65px;
          height: $size;
          width: $size;
          border-radius: 20%;
          border: 1px solid gainsboro;
          object-fit: contain;
        }

        h1 {
          margin-left: 20px;
          font-size: 1.3rem;
          line-height: 1.9rem;
          padding-right: 200px;
        }
      }

      .job-company-detail {
        display: flex;
        align-items: center;
        margin-top: 15px;

        svg {
          $size: 16px;
          height: $size;
          width: $size;
          fill: var(--textMedium);
          margin-right: 15px;
        }
      }

      .one-line {
        width: 100%;
      }

      &-btn {
        $margin: calc($padding + 5px);
        position: absolute;
        top: $margin;
        right: $margin;
        display: flex;
        align-items: center;
        color: var(--purple);
        font-weight: 500;
        transition: color 0.3s ease-out;

        svg {
          $size: 15px;
          height: $size;
          width: $size;
          fill: var(--purple);
          margin-left: 10px;
          transition:
            fill 0.3s ease-out,
            transform 0.3s ease-out;
        }
      }
    }
  }
}

@media (max-width: 1100px) {
  #job {
    width: auto;
    padding-right: 50px;
    padding-left: 50px;
  }
}

@media (max-width: 900px) {
  #job {
    padding-right: 20px;
    padding-left: 20px;

    &-header {
      display: block;

      &-btns {
        margin-top: 40px;
      }
    }

    &-content {
      display: block;

      &-description {
        font-size: 1.1rem;
        line-height: 1.7rem;
        padding-bottom: 50px;
        margin-bottom: 30px;
        border-bottom: 1px solid gainsboro;

        &-btn {
          margin: 30px auto 0;
        }
      }
    }

    &-company {
      width: 100%;
    }
  }
}

@media (max-width: 600px) {
  #job {
    &-header {
      padding-top: 20px;
      padding-bottom: 35px;

      .breadcrumbs {
        margin: 0;
      }

      &-btns {
        padding: 0;
        margin-top: 30px;
      }
    }

    &-overview {
      margin-top: 40px;

      h1 {
        font-size: 1.7rem;
        line-height: 2.2rem;
      }

      &-employer {
        margin-top: 20px;

        img {
          $size: 35px;
          height: $size;
          width: $size;
        }
      }

      &-summary {
        margin-top: 15px;
      }
    }

    &-content {
      padding-top: 30px;

      &-description {
        padding-bottom: 40px;
      }
    }

    &-company {
      &-card {
        padding: 22px;
        background-color: var(--lightPurple);
        border: none;
        border-radius: 15px;
        box-shadow: 0 7px 15px rgba(174, 157, 174, 0.5);

        &-title {
          margin-bottom: 10px;

          img {
            $size: 55px;
            height: $size;
            width: $size;
            width: auto;
            max-width: 90px;
            border-radius: 10px;
            border: none;
          }

          h1 {
            padding-right: 0;
            font-size: 1.1rem;
            line-height: 1.6rem;
            margin-left: 10px;
          }
        }

        &-btn {
          position: relative;
          top: 0;
          right: 0;
          width: 100%;
          margin-top: 25px;
          margin-bottom: 5px;
        }

        .job-company-detail {
          margin-top: 8px;
          font-size: 0.95rem;
        }
      }
    }
  }
}

@media (hover: hover) {
  #job {
    &-header {
      &-btns {
        .btn-black-outline:hover {
          border-color: var(--purple);
          background-color: transparent;
          color: var(--purple);

          svg {
            fill: var(--purple);
          }
        }

        .btn-black:hover {
          background-color: var(--purple);
          color: white;
          border-color: var(--purple);
        }
      }
    }

    &-overview {
      &-employer:hover {
        span {
          color: var(--purple);
        }
      }
    }

    &-company {
      &-card:hover {
        border-color: var(--purple);

        #job-company-card-btn {
          color: var(--black);

          svg {
            fill: var(--black);
            transform: translateX(5px);
          }
        }
      }
    }
  }
}
</style>
